import { useEffect, useState } from 'react';
import { isPageStale } from '../../features/settings/pageStale.ts';
import { useNotice } from './notices.ts';
import type { Notice } from './notices.ts';

const PAGE_SHA = typeof __ORGUS_BUILD_SHA__ === 'string' ? __ORGUS_BUILD_SHA__ : '';

/** What GET /api/health says about builds, as this notice reads it. */
interface HealthBuild {
  sha: string | null;
  page: string | null;
  /** The bundle the hub serves is older than the web sources in its checkout (QA-38). */
  web: { page: string; code: string } | null;
}

/** The notice for a hub on other code than the page, or null when they match (or cannot be compared). */
export function staleHubNotice(pageSha: string, hubSha: string | null, reload: () => void, servedPage: string | null = null): Notice | null {
  if (!isStaleHub(pageSha, hubSha, servedPage)) return null;
  return {
    id: 'shell.stale-hub',
    tone: 'attention',
    kind: 'build',
    title: 'The hub runs different code than this page',
    body: 'Restart the hub with orgus up, then reload this page.',
    detail: `Hub ${hubSha}, page ${pageSha}`,
    actions: [{ id: 'reload', label: 'Reload page', run: reload, emphasis: 'secondary' }],
  };
}

/**
 * True only when both commits are known and differ. A hub that could not read its own
 * commit (git was slow or missing when it started) reports none: that is not a mismatch.
 * Nor is a page that is the very bundle the hub serves: if that is old, staleWebNotice says so.
 */
export function isStaleHub(pageSha: string, hubSha: string | null, servedPage: string | null = null): boolean {
  return isPageStale(pageSha, { sha: hubSha || null, page: servedPage });
}

/**
 * The quiet notice for a hand-started hub serving a bundle older than the web sources in its
 * checkout (QA-38): only follow-main rebuilds it. Null when the bundle is current or unknown.
 */
export function staleWebNotice(web: HealthBuild['web'] | undefined): Notice | null {
  if (!web?.page || !web.code) return null;
  return {
    id: 'shell.stale-web',
    tone: 'neutral',
    kind: 'build',
    title: `This console was built from ${web.page}; the code is at ${web.code}.`,
    body: 'Rebuild with pnpm build.',
  };
}

/**
 * Notices when the hub runs older (or other) code than this page was built from, which makes
 * new pages call routes the hub does not have yet, and when the bundle itself is older than the
 * code. Checks on load and every minute. They show in the top bar's notices item, not as a
 * banner: the page itself still works.
 */
export function StaleHubNotice() {
  const [build, setBuild] = useState<HealthBuild | null>(null);
  useEffect(() => {
    let stop = false;
    const check = async () => {
      try {
        const r = await fetch('/api/health', { cache: 'no-store' });
        const body = (await r.json()) as { build?: Partial<HealthBuild> };
        if (!stop) setBuild({ sha: body.build?.sha ?? '', page: body.build?.page ?? null, web: body.build?.web ?? null });
      } catch {
        /* the connection indicator already covers an unreachable hub */
      }
    };
    void check();
    const t = setInterval(check, 60_000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, []);
  useNotice(build ? staleHubNotice(PAGE_SHA, build.sha, () => window.location.reload(), build.page) : null);
  useNotice(staleWebNotice(build?.web));
  return null;
}
